import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'components/universal/button';
import Heading from 'components/universal/heading';
import { Input } from 'components/universal/input';
import { Modal } from 'components/universal/modal';
import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { hideModal } from 'store/modal/modalSlice';
import Paragraph from '../paragraph';
import cls from './form-modal.module.scss';
import { schema } from './validation';
import cities from '../../../utils/cities.json';
import { components } from 'react-select';
import { ReactComponent as ArrowDown } from '../../../assets/images/icon/icon-down.svg';

const DropdownIndicator = (props) => {
    return (
        <>
            <components.DropdownIndicator {...props}>
                <ArrowDown />
            </components.DropdownIndicator>
        </>
    );
};

export interface IRegistrationFormValuesFormModal {
    workplace_name: string;
    workplace_address_street: string;
    workplace_address_number: string;
    workplace_address_city: string;
    workplace_address_postcode: string;
}

const defaultValues = {
    workplace_name: '',
    workplace_address_postcode: '',
    workplace_address_city: '',
    workplace_address_street: '',
    workplace_address_number: ''
};

interface IProps {
    setValueForm: (formObject: IRegistrationFormValuesFormModal) => void;
};

export const FormModal: FC<IProps> = ({ setValueForm }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
        control
    } = useForm<IRegistrationFormValuesFormModal>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues,
    });

    const onSubmit = (formObject) => {
        setValueForm(formObject);
        dispatch(hideModal());
    };

    const onBack = () => {
        dispatch(hideModal());
    }

    useEffect(() => {
        dispatch(hideModal());
    }, []);

    const formatZipCode = (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 5) value = value.slice(0, 5);
        if (value.length > 2) value = `${value.slice(0, 2)}-${value.slice(2)}`;
        e.target.value = value;
    };

    return (
        <Modal className={cls.modal}>
            <form className={cls.form} onSubmit={handleSubmit(onSubmit)}>
                <Heading 
                    headingLevel="h3" 
                    className={`${cls.heading2} mb-5`}
                >
                    {t('registration.addNewWorkplace')}
                </Heading>
                <Paragraph 
                    className={`${cls.paragraph} mb-10`}
                >
                    {t('registration.addNewWorkplaceDescription')}
                </Paragraph>

                <div className={cls.formFields}>
                    <Input
                        className={`${cls.input} mb-6 ${cls.inputFullWidth}`}
                        register={register}
                        id="workplace_name"
                        name="workplace_name"
                        label={t('registration.workplaceNameRequire')}
                        error={t(
                            errors.workplace_address_postcode?.message
                        )}
                        maxLength={255}
                    />
                    <Input
                        className={`${cls.input} mb-6`}
                        register={register}
                        id="workplace_address_postcode"
                        name="workplace_address_postcode"
                        label={t('registration.zipCodeRequire')}
                        error={t(
                            errors.workplace_address_postcode?.message
                        )}
                        maxLength={6}
                        onChange={formatZipCode}
                    />
                    <div className={cls.select}>
                        <label htmlFor="workplace_address_city" className={cls.label}>
                            {t('registration.cityRequire')}
                        </label>
                        <Controller
                            name="workplace_address_city"
                            control={control}
                            render={({ field }) => (
                            <Select
                                {...field}
                                options={cities}
                                className={errors.workplace_address_city ? 'error react-select-container' : 'react-select-container'}
                                classNamePrefix="react-select"
                                placeholder=""
                                components={{ DropdownIndicator }}
                                onChange={(selected) => field.onChange(selected?.value)}
                                value={cities.find((c) => c.value === field.value) || null}
                                noOptionsMessage={() => t('registration.noOptions')}
                                maxMenuHeight={200}
                            />
                            )}
                        />
                    </div>
                    <Input
                        className={`${cls.input} mb-6`}
                        register={register}
                        id="workplace_address_street"
                        name="workplace_address_street"
                        label={t('registration.streetRequire')}
                        error={t(errors.workplace_address_street?.message)}
                        maxLength={255}
                    />
                    <Input
                        className={`${cls.input} mb-6`}
                        register={register}
                        id="workplace_address_number"
                        name="workplace_address_number"
                        label={t('registration.apartmentNumberRequire')}
                        error={t(errors.workplace_address_number?.message)}
                        maxLength={255}
                    />
                    
                </div>
                <div className={cls.buttonWrapper}>
                    <Button 
                        className={`${cls.button} mx-auto mb-5`} 
                        styleType="border"
                        onClick={onBack}
                        type="button"
                    >
                        {t('registration.cancel')}
                    </Button>
                    <Button className={cls.button} type="submit">
                        {t('registration.saveWorkplace')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
